/**
 * `$…$` / `$$…$$` → math tokens.
 *
 * The hard problem is not parsing math, it is refusing prose. This content is
 * full of prices, so nearly every `$` on the page is currency, and any two of
 * them can pair up across a whole sentence. That is not hypothetical: a hotel
 * shortlist rendered `$257/night) — **do not book via Expedia…parking ($)` as
 * one math span, because the price opened it and an unrelated `($)` closed it.
 *
 * So a candidate must clear four independent gates before it is claimed, none
 * of which is sufficient alone:
 *
 * 1. DELIMITER SHAPE — a price may neither open nor close a span, and a span
 *    may not cross a `$` or a line break.
 * 2. BLAST RADIUS — a span has a length ceiling, so the worst case of a
 *    mispaired `$` is bounded.
 * 3. PROSE MARKERS — markdown emphasis and English word runs are structural
 *    evidence of prose, and KaTeX cannot supply it: `**bold**` renders as
 *    `∗∗bold∗∗` rather than failing, and English letters are all valid
 *    variables.
 * 4. KATEX — `isRenderableLatex` under `strict: 'error'`, which rejects
 *    unknown symbols and Unicode text in math mode.
 *
 * `$$…$$` is deliberate — nobody types it for currency — so it clears gates 2
 * and 4 only. That also makes it the escape hatch for any genuine formula the
 * inline gates turn away.
 */

import type { TokenizerExtension } from 'marked';
import {
  MATH_BLOCK_TOKEN_NAME,
  MATH_INLINE_TOKEN_NAME,
  type MathBlockToken,
  type MathInlineToken,
} from './math-types';
import { isRenderableLatex } from './katex-renderer';

const MATH_BLOCK_PATTERN = /^\$\$([\s\S]+?)\$\$/;
const MATH_INLINE_DISPLAY_PATTERN = /^\$\$([\s\S]+?)\$\$/;
// Single-`$` inline math must stay on one line: `[^$\n]` (not `[^$]`) stops a
// stray `$` in prose from swallowing whole paragraphs before validation.
//
// BOTH delimiters carry a currency guard, because either half of a price can be
// the one that pairs. `(?!\d)` after the opener means `$257/night` never OPENS a
// span — the half that was missing, and the whole of the live defect. `(?!\d)`
// after the closer is Pandoc's rule and means `$30-$40` never CLOSES one. The
// capture class rejects whitespace against either delimiter, so a shell-style
// `$HOME … $PATH` cannot pair either.
//
// The cost is a digit-leading inline formula: `$2x+1$` is no longer math. Write
// it as `$$2x+1$$`, which is exempt from the currency gates entirely.
const MATH_INLINE_PATTERN = /^\$(?!\d)([^\s$\n](?:[^$\n]*?[^\s$\n])?)\$(?!\d)/;
const MARKED_BLOCK_TOKEN_LEVEL = 'block';
const MARKED_INLINE_TOKEN_LEVEL = 'inline';

/**
 * Blast radius for a mispaired `$`. Inline math is an expression, not a
 * paragraph; the live defect claimed roughly 250 characters of prose.
 */
const MAX_INLINE_MATH_CHARS = 200;
/** The same ceiling for `$$…$$`, which legitimately carries multi-line formulas. */
const MAX_DISPLAY_MATH_CHARS = 2000;

/**
 * Markdown emphasis inside a candidate. KaTeX reads `*` as an operator and
 * renders `**bold**` without complaint, so this has to be caught structurally.
 */
const MARKDOWN_EMPHASIS_PATTERN = /\*\*|__/;
/** `\frac`, `\alpha`, `\operatorname` — removed before prose detection reads letters. */
const CONTROL_SEQUENCE_PATTERN = /\\[a-zA-Z]+/g;
/**
 * A bare multi-letter run. LaTeX spells its own multi-letter names with a
 * backslash and reads `abc` as a·b·c, so bare runs are English words.
 */
const BARE_WORD_PATTERN = /[a-zA-Z]{3,}/g;
/**
 * Two bare words still read as math — `x_{min} + y_{max}`, `\text{a b}`. Three
 * is a sentence.
 */
const MAX_BARE_WORDS = 2;
/** A paragraph break inside a display span means the `$$` pair joined unrelated blocks. */
const BLANK_LINE_PATTERN = /\n[ \t]*\n/;
/** A `$` that opens nothing: currency (`$257`) or a dangling one (`$ `). */
const NON_OPENING_DOLLAR_PATTERN = /^\$[\d\s]/;

function isEscapedDollar(input: string, index: number): boolean {
  let backslashCount = 0;

  for (let currentIndex = index - 1; currentIndex >= 0 && input[currentIndex] === '\\'; currentIndex -= 1) {
    backslashCount += 1;
  }

  return backslashCount % 2 === 1;
}

function countBareWords(input: { latex: string }): number {
  const withoutCommands = input.latex.replace(CONTROL_SEQUENCE_PATTERN, ' ');
  return (withoutCommands.match(BARE_WORD_PATTERN) ?? []).length;
}

/** Structural evidence that a span is a sentence a stray `$` reached across. */
function isProse(input: { latex: string }): boolean {
  return MARKDOWN_EMPHASIS_PATTERN.test(input.latex)
    || countBareWords({ latex: input.latex }) > MAX_BARE_WORDS;
}

function isInlineMath(input: { latex: string }): boolean {
  if (input.latex.length > MAX_INLINE_MATH_CHARS) {
    return false;
  }

  if (isProse({ latex: input.latex })) {
    return false;
  }

  return isRenderableLatex({ latex: input.latex, displayMode: false });
}

function isDisplayMath(input: { latex: string }): boolean {
  if (input.latex.length > MAX_DISPLAY_MATH_CHARS) {
    return false;
  }

  if (BLANK_LINE_PATTERN.test(input.latex)) {
    return false;
  }

  return isRenderableLatex({ latex: input.latex, displayMode: true });
}

function findFirstInlineMathDelimiter(src: string): number | undefined {
  // Both `$…$` and `$$…$$` are valid inline starts — the latter is matched
  // here (rather than only block-level) so mid-paragraph display math like
  // `expressed as:$$r=2GM/c^2$$ where:$G$…` renders.
  //
  // A `$` that cannot open a span under MATH_INLINE_PATTERN is skipped rather
  // than reported, so a paragraph of prices never fragments the text token it
  // belongs to. `$$` survives the check: the char after the first `$` is `$`.
  for (let index = src.indexOf('$'); index >= 0; index = src.indexOf('$', index + 1)) {
    if (isEscapedDollar(src, index) || NON_OPENING_DOLLAR_PATTERN.test(src.slice(index))) {
      continue;
    }
    return index;
  }

  return undefined;
}

function findFirstBlockMathDelimiter(src: string): number | undefined {
  for (let index = src.indexOf('$$'); index >= 0; index = src.indexOf('$$', index + 2)) {
    if (isEscapedDollar(src, index)) {
      continue;
    }

    return index;
  }

  return undefined;
}

export function createMathBlockTokenizerExtension(): TokenizerExtension {
  return {
    name: MATH_BLOCK_TOKEN_NAME,
    level: MARKED_BLOCK_TOKEN_LEVEL,
    start(src) {
      return findFirstBlockMathDelimiter(src);
    },
    tokenizer(src) {
      const match = MATH_BLOCK_PATTERN.exec(src);
      if (!match) {
        return undefined;
      }

      const text = match[1].trim();
      if (!text) {
        return undefined;
      }

      if (!isDisplayMath({ latex: text })) {
        return undefined;
      }

      const token: MathBlockToken = {
        type: MATH_BLOCK_TOKEN_NAME,
        raw: match[0],
        text,
        displayMode: true,
      };

      return token;
    },
  };
}

export function createMathInlineTokenizerExtension(): TokenizerExtension {
  return {
    name: MATH_INLINE_TOKEN_NAME,
    level: MARKED_INLINE_TOKEN_LEVEL,
    start(src) {
      return findFirstInlineMathDelimiter(src);
    },
    tokenizer(src) {
      // `$$…$$` mid-paragraph: the block-level tokenizer only fires at the
      // start of a block, so when authors put `$$…$$` inline (e.g. inside a
      // list item, after a colon, etc.), it must be matched here. Render it
      // with `displayMode: true` to keep KaTeX block-style formatting.
      if (src.startsWith('$$')) {
        const displayMatch = MATH_INLINE_DISPLAY_PATTERN.exec(src);
        if (!displayMatch) {
          return undefined;
        }
        const displayText = displayMatch[1].trim();
        if (!displayText) {
          return undefined;
        }
        if (!isDisplayMath({ latex: displayText })) {
          return undefined;
        }
        const displayToken: MathInlineToken = {
          type: MATH_INLINE_TOKEN_NAME,
          raw: displayMatch[0],
          text: displayText,
          displayMode: true,
        };
        return displayToken;
      }

      const match = MATH_INLINE_PATTERN.exec(src);
      if (!match) {
        return undefined;
      }

      const text = match[1].trim();
      if (!text) {
        return undefined;
      }

      if (!isInlineMath({ latex: text })) {
        return undefined;
      }

      const token: MathInlineToken = {
        type: MATH_INLINE_TOKEN_NAME,
        raw: match[0],
        text,
        displayMode: false,
      };

      return token;
    },
  };
}
